import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useCallback,
  MouseEvent,
  ReactNode,
} from "react";
import clsx from "clsx";

import {
  TransactionType,
  CategoriesModel,
  CategorySelectorModel,
} from "@interfaces/index";
import {
  ChevronDownSvg,
  TrashXSvg,
  SearchSvg,
  ChevronSmallDownSvg,
  Button,
  ChecksSvg,
  TrashSvg,
  XSvg,
} from "@components/index";

interface ListItem {
  type: TransactionType;
  categories: {
    uniqueId: string;
    title: string;
    subcategories: {
      uniqueId: string;
      title: string;
    }[];
  }[];
}

export const CategorySelector = ({
  label = "Select category...",
  height = "medium",
  variant = "formField",
  categories,
  value,
  dataTestid,
  onSetCategory,
  error,
}: {
  label?: string | ReactNode;
  height?: "small" | "medium";
  variant?: "filter" | "formField";
  categories?: CategoriesModel;
  value?: CategorySelectorModel;
  dataTestid?: string;
  onSetCategory: (value?: CategorySelectorModel) => void;
  error?: string;
}) => {
  const [list, setList] = useState<ListItem[]>([]);
  const [searchValue, setSearchValue] = useState("");

  const [isShown, setIsShown] = useState(false);

  const [typeId, setTypeId] = useState<TransactionType | "">();
  const [categoryId, setCategoryId] = useState("");
  const [categoryTitle, setCategoryTitle] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [subcategoryTitle, setSubcategoryTitle] = useState("");

  const [item, setItem] = useState<CategorySelectorModel>();
  const [buttonLabel, setButtonLabel] = useState("");

  let heightStyles;
  switch (height) {
    case "small":
      heightStyles = "h-12 py-3";
      break;
    case "medium":
    default:
      heightStyles = "h-14 py-4";
      break;
  }

  const initValues = useCallback(() => {
    let label = "";
    if (value?.type) {
      setTypeId(value.type);
      label += `${value.type[0].toUpperCase()}${value.type.slice(1)}`;
    }
    if (value?.categoryId && value?.type) {
      const foundCategory = categories?.[value.type].find(
        (category) => category.uniqueId === value.categoryId,
      );
      if (foundCategory) {
        setCategoryId(foundCategory.uniqueId);
        setCategoryTitle(foundCategory.title);
        label += variant === "formField" ? " : " : " • ";
        label += foundCategory.title;
      } else {
        const foundCategory = categories?.[value.type].find((category) =>
          category.subcategories.find(
            (subcategory) => subcategory.uniqueId === value.categoryId,
          ),
        );
        setCategoryId(foundCategory?.uniqueId || "");
        setCategoryTitle(foundCategory?.title || "");
        if (foundCategory?.title) {
          label += variant === "formField" ? " : " : " • ";
          label += foundCategory.title;
        }
        const foundSubcategory = foundCategory?.subcategories.find(
          (subcategory) => subcategory.uniqueId === value.categoryId,
        );
        setSubcategoryId(foundSubcategory?.uniqueId || "");
        setSubcategoryTitle(foundSubcategory?.title || "");
        if (foundSubcategory?.title) {
          label += variant === "formField" ? "/" : " • ";
          label += foundSubcategory.title;
        }
      }
    }
    if (!value) {
      setTypeId(undefined);
      setCategoryId("");
      setCategoryTitle("");
      setSubcategoryId("");
      setSubcategoryTitle("");
    }
    setButtonLabel(label);
  }, [variant, categories, value]);

  const getCategoriesList = useCallback((): ListItem[] => {
    const value = searchValue.toLocaleLowerCase();

    const oneList: ListItem[] = [
      {
        type: "income",
        categories: (categories?.income ? categories.income : []).map(
          (category) => ({
            uniqueId: category.uniqueId,
            title: category.title,
            subcategories: category.subcategories.map((subcategory) => ({
              uniqueId: subcategory.uniqueId,
              title: subcategory.title,
            })),
          }),
        ),
      },
      {
        type: "expense",
        categories: (categories?.expense ? categories.expense : []).map(
          (category) => ({
            uniqueId: category.uniqueId,
            title: category.title,
            subcategories: category.subcategories.map((subcategory) => ({
              uniqueId: subcategory.uniqueId,
              title: subcategory.title,
            })),
          }),
        ),
      },
    ];

    const filteredList = oneList.map((item) => ({
      ...item,
      categories: item.categories
        .filter(
          (category) =>
            category.title.toLocaleLowerCase().includes(value) ||
            category.subcategories.find(({ title }) =>
              title.toLocaleLowerCase().includes(value),
            ),
        )
        .map((category) => ({
          ...category,
          subcategories: category.subcategories
            .filter(
              ({ title }) =>
                category.title.toLocaleLowerCase().includes(value) ||
                title.toLocaleLowerCase().includes(value),
            )
            .map((subcategory) => ({ ...subcategory })),
        })),
    }));

    return filteredList;
  }, [categories, searchValue]);

  const itemLabel = useMemo(() => {
    setItem({
      type: typeId || "",
      categoryId: subcategoryId || categoryId || "",
    });
    if (variant === "formField") {
      return subcategoryTitle;
    }

    let label = "";
    label += typeId ? `${typeId[0].toUpperCase()}${typeId.slice(1)}` : "";
    label += categoryId ? ` > ${categoryTitle}` : "";
    label += subcategoryId ? ` > ${subcategoryTitle}` : "";
    return label;
  }, [
    variant,
    typeId,
    categoryId,
    categoryTitle,
    subcategoryId,
    subcategoryTitle,
  ]);

  useEffect(() => {
    setList(getCategoriesList());
    initValues();
  }, [initValues, getCategoriesList]);

  const handleClickType = (value: TransactionType | "") => {
    setTypeId(typeId === value ? "" : value);
    setCategoryId("");
    setCategoryTitle("");
    setSubcategoryId("");
    setSubcategoryTitle("");
  };

  const handleClickCategory = (
    id: string,
    title: string,
    type: TransactionType,
  ) => {
    setTypeId(type);
    setCategoryId(categoryId === id ? "" : id);
    setCategoryTitle(categoryId === id ? "" : title);
    setSubcategoryId("");
    setSubcategoryTitle("");
  };

  const handleClickSubcategory = (
    id: string,
    title: string,
    type: TransactionType,
    catId: string,
    catTitle: string,
  ) => {
    if (variant === "formField") {
      setTypeId(type);
      setCategoryId(catId);
      setCategoryTitle(catTitle);
      setSubcategoryId(id);
      setSubcategoryTitle(title);
      handleChoose(id, type);
    } else {
      setTypeId(subcategoryId === id ? "" : type);
      setCategoryId(subcategoryId === id ? "" : catId);
      setCategoryTitle(subcategoryId === id ? "" : catTitle);
      setSubcategoryId(subcategoryId === id ? "" : id);
      setSubcategoryTitle(subcategoryId === id ? "" : title);
    }
  };

  const handleClearData = () => {
    setSearchValue("");
    setTypeId(undefined);
    setCategoryId("");
    setCategoryTitle("");
    setSubcategoryId("");
    setSubcategoryTitle("");
    setItem(undefined);
  };

  const handleOpen = () => {
    initValues();
    setIsShown(true);
  };

  const handleChoose = (id?: string, type?: TransactionType) => {
    if (variant === "formField" && id) {
      onSetCategory({
        type: type || "",
        categoryId: id,
      });
    } else {
      onSetCategory(item);
    }
    setIsShown(false);
    handleClearData();
    setButtonLabel(itemLabel);
  };

  const handleClose = () => {
    handleClearData();
    setIsShown(false);
  };

  const handleClear = () => {
    handleClearData();
    setIsShown(false);
    onSetCategory(undefined);
  };

  const handleClearDropdownValue = (event: MouseEvent) => {
    event.stopPropagation();
    handleClearData();
    onSetCategory(undefined);
  };

  return (
    <>
      <div className="flex flex-col" data-testid={dataTestid}>
        <button
          type="button"
          value={buttonLabel}
          onClick={handleOpen}
          className={clsx(
            "flex items-center justify-between rounded-lg border border-black-16 bg-white-100 px-4 hover:border-black-32",
            heightStyles,
            buttonLabel ? "text-black-100" : "text-black-54",
            error && "border-red-100 text-red-100",
          )}
        >
          <div className="truncate">{buttonLabel || label}</div>
          <div className="shrink-0">
            {(value?.type || value?.categoryId) && !isShown ? (
              <div onClick={handleClearDropdownValue}>
                <TrashXSvg />
              </div>
            ) : (
              <ChevronDownSvg />
            )}
          </div>
        </button>
        {error && <p className="pl-4 pt-1 text-100 text-red-100">{error}</p>}
      </div>

      {isShown && (
        <div
          className="fixed inset-0 z-10 flex items-center justify-center bg-black-54 p-4"
          onClick={handleClose}
        >
          <div
            className="w-full max-w-2xl rounded-lg bg-white-100 p-6 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex w-[calc(100%_-_50px)] items-center justify-between gap-x-4 rounded-lg border border-black-16 px-4 py-2 hover:border-black-32 focus:border-black-32 md:py-3">
                <input
                  type="text"
                  value={searchValue}
                  onChange={({ target }) => setSearchValue(target.value)}
                  placeholder="Search"
                  className="w-full text-black-100 outline-none"
                />
                <div className="text-black-54">
                  <SearchSvg />
                </div>
              </div>
              <button type="button" onClick={handleClose}>
                <XSvg />
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              <div className="mt-2 flex flex-col pr-3">
                {list.map(({ type, categories }, index) => (
                  <Fragment key={type}>
                    <div
                      className={clsx(
                        "flex cursor-pointer items-center justify-between py-3 text-300 capitalize",
                        type === typeId
                          ? "font-light text-black-54"
                          : "font-bold text-black-100",
                      )}
                      onClick={() => handleClickType(type)}
                    >
                      {type}
                      {type === typeId ? ":" : ""}
                      <div
                        className={clsx(
                          "text-primary-300",
                          type === typeId && "hidden",
                        )}
                      >
                        <ChevronSmallDownSvg />
                      </div>
                    </div>
                    {(type === typeId || searchValue) &&
                      categories.map(({ uniqueId, title, subcategories }) => (
                        <Fragment key={uniqueId}>
                          <div
                            className={clsx(
                              "flex cursor-pointer items-center justify-between py-3 text-300 font-bold",
                              uniqueId === categoryId
                                ? "text-primary-300"
                                : "text-black-100",
                            )}
                            onClick={() =>
                              handleClickCategory(uniqueId, title, type)
                            }
                          >
                            {title}
                            <div
                              className={clsx(
                                "text-primary-300 duration-300",
                                uniqueId === categoryId && "rotate-180",
                              )}
                            >
                              <ChevronSmallDownSvg />
                            </div>
                          </div>
                          {(uniqueId === categoryId || searchValue) &&
                            subcategories.map((subcategory) => (
                              <div
                                key={subcategory.uniqueId}
                                className={clsx(
                                  "flex cursor-pointer items-center gap-x-4 py-3 text-300 font-light",
                                  subcategory.uniqueId === subcategoryId
                                    ? "text-primary-300"
                                    : "text-black-70",
                                )}
                                onClick={() =>
                                  handleClickSubcategory(
                                    subcategory.uniqueId,
                                    subcategory.title,
                                    type,
                                    uniqueId,
                                    title,
                                  )
                                }
                              >
                                <div className="h-[6px] w-[7px] border-b border-l border-black-24" />
                                {subcategory.title}
                              </div>
                            ))}
                        </Fragment>
                      ))}
                    {index < list.length - 1 && (
                      <div className="my-2 h-px bg-black-8" />
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
            {variant === "filter" && (
              <div className="flex justify-between pt-6">
                <Button
                  title="Apply"
                  leftIcon={<ChecksSvg />}
                  height="small"
                  disabled={!itemLabel}
                  onClick={handleChoose}
                />
                <Button
                  title="Clear all"
                  leftIcon={<TrashSvg />}
                  variant="text"
                  height="small"
                  onClick={handleClear}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
